import React, { useState } from 'react';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { heroServices, serviceCategories } from '../mock';
import { useToast } from '../hooks/use-toast';
import { submitEnquiry } from '../api';

export default function Hero() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', mobile: '', service: '', message: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast({ title: 'Missing details', description: 'Please provide your name and mobile number.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, source: 'hero_form' });
      toast({ title: 'Request received!', description: `Thanks ${form.name}, our expert will call you shortly.` });
      setForm({ name: '', email: '', mobile: '', service: '', message: '' });
      setOtpSent(false);
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or call us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-gradient-to-br from-[#f4f9fd] via-white to-[#fff4ec] border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-start">
        {/* Left */}
        <div>
          <div className="inline-flex items-center gap-2 bg-white border border-orange-200 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm mb-5">
            <ShieldCheck className="w-4 h-4" /> Trusted by 10,000+ Businesses across India
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#0b3d5b] leading-tight tracking-tight">
            Talk to CA/CS Instantly at India&apos;s most trusted <span className="text-orange-600">Tax &amp; Compliance</span> Platform
          </h1>
          <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
            Register Point brings you a verified network of top Chartered Accountants, Company Secretaries and Tax Experts to solve everything your business needs — online, fast and affordable.
          </p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {heroServices.map((s) => (
              <li key={s} className="flex items-start gap-2 text-slate-700 text-sm md:text-[15px]">
                <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Right - Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 md:p-8">
          <div className="text-xs font-semibold uppercase tracking-widest text-orange-600">For Expert Consultancy</div>
          <h3 className="text-2xl font-bold text-[#0b3d5b] mt-1">Get in Touch</h3>
          <p className="text-sm text-slate-500 mt-1">Fill the form &mdash; our expert will call you back.</p>
          <form onSubmit={submit} className="mt-5 space-y-3">
            <input
              type="text"
              placeholder="Full Name*"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
            />
            <input
              type="email"
              placeholder="Email Address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
            />
            <div className="flex gap-2">
              <input
                type="tel"
                placeholder="Mobile Number*"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                className="flex-1 px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  if (!form.mobile) return;
                  setOtpSent(true);
                }}
                className="px-4 py-2.5 rounded-lg bg-[#0b3d5b] hover:bg-[#0d4a6f] text-white text-sm font-semibold transition-colors whitespace-nowrap"
              >
                {otpSent ? 'Sent' : 'Get OTP'}
              </button>
            </div>
            <select
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white text-slate-700"
            >
              <option value="">Select Service</option>
              {serviceCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <textarea
              placeholder="Tell us briefly about your requirement"
              rows="3"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm resize-none"
            />
            <button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70 text-white font-semibold py-3 rounded-lg transition-colors">
              {submitting ? 'Sending…' : 'Get Started'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
